import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from '../../../i18n/useTranslation';
import { useDisplayName } from '../../../Utils/useDisplayName';
import { WEEKDAY_KEYS } from '../../../i18n/weekdays';
import { ApiError } from '../../../Models/ApiModel';
import { PremiumFeature, type Weekday } from '../../../Models/enums';
import type { MonthListEntry, MonthlyReportModel } from '../../../Models/ReportModel';
import { ReportService } from '../../../Services/ReportService';
import { formatMonth } from '../../../Utils/DateUtil';
import { MoneyText } from '../../SharedArea/MoneyText/MoneyText';
import { Spinner } from '../../SharedArea/Spinner/Spinner';
import { ErrorState } from '../../SharedArea/ErrorState/ErrorState';
import { EmptyState } from '../../SharedArea/EmptyState/EmptyState';
import { PremiumGate } from '../Premium/PremiumGate';
import './Reports.css';
import { PremiumSubnav } from '../Premium/PremiumSubnav';

/**
 * How a month closed.
 *
 * The three numbers that matter lead: what was billed, what came in, and what
 * is still out. Everything under them explains one of those three.
 */
export function Reports() {
  return (
    <PremiumGate feature={PremiumFeature.AdvancedReports}>
      <ReportsInner />
    </PremiumGate>
  );
}

function ReportsInner() {
  const { tr, language } = useTranslation();
  const displayName = useDisplayName();
  const [months, setMonths] = useState<MonthListEntry[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [report, setReport] = useState<MonthlyReportModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    ReportService.listMonths()
      .then(result => {
        setMonths(result.months);
        setSelected(result.months[0]?.month ?? null);
      })
      .catch(failure => setError(failure instanceof ApiError ? failure.code : 'SERVER_ERROR'))
      .finally(() => setLoading(false));
  }, []);

  const load = useCallback(async (month: string) => {
    setLoading(true);
    setError(null);
    try {
      setReport(await ReportService.month(month));
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.code : 'SERVER_ERROR');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selected) void load(selected);
  }, [selected, load]);

  if (loading && !report) return <Spinner />;
  if (error) return <ErrorState code={error} onRetry={() => selected && void load(selected)} />;
  if (months.length === 0) return <EmptyState title={tr('noReports')} hint={tr('noReportsHint')} />;

  return (
    <section className="reports">
      <header className="reports-header">
        <PremiumSubnav />
        <h1 className="screen-title">{tr('navReports')}</h1>
        <label className="field">
          <span>{tr('reportMonth')}</span>
          <select
            className="text-input"
            value={selected ?? ''}
            onChange={event => setSelected(event.target.value)}
          >
            {months.map(entry => (
              <option key={entry.month} value={entry.month}>
                {formatMonth(`${entry.month}-01`, language)}
              </option>
            ))}
          </select>
        </label>
      </header>

      {report && (
        <>
          <div className="dashboard-figures">
            <article className="figure">
              <p className="figure-label">{tr('reportBilled')}</p>
              <MoneyText amount={report.summary.billed} className="figure-value" />
            </article>
            <article className="figure is-collected">
              <p className="figure-label">{tr('income')}</p>
              <MoneyText amount={report.summary.collected} className="figure-value" />
            </article>
            <article className="figure is-owed">
              <p className="figure-label">{tr('debt')}</p>
              <MoneyText amount={report.summary.outstanding} className="figure-value" />
            </article>
          </div>

          <section className="dashboard-panel">
            <h2>{tr('reportSummaryTitle')}</h2>
            <ul className="reports-facts">
              <li>
                <span>{tr('reportLessons')}</span>
                <bdi>{report.summary.lessons}</bdi>
              </li>
              <li>
                <span>{tr('reportCollectionRate')}</span>
                <bdi>{report.summary.collectionRate === null ? '·' : `${Math.round(report.summary.collectionRate)}%`}</bdi>
              </li>
              <li>
                <span>{tr('reportActiveStudents')}</span>
                <bdi>{report.summary.activeStudents}</bdi>
              </li>
              <li>
                <span>{tr('reportVsPrevious')}</span>
                <bdi className={report.previous.billedDelta >= 0 ? 'is-up' : 'is-down'}>
                  {report.previous.billedDeltaPercent === null
                    ? '·'
                    : `${report.previous.billedDeltaPercent > 0 ? '+' : ''}${Math.round(report.previous.billedDeltaPercent)}%`}
                </bdi>
              </li>
            </ul>
          </section>

          {report.receipts.missingCount > 0 && (
            <section className="dashboard-panel reports-warning">
              <h2>{tr('reportMissingReceipts')}</h2>
              <p>
                <bdi>{report.receipts.missingCount}</bdi> · <MoneyText amount={report.receipts.missingTotal} />
              </p>
            </section>
          )}

          <section className="dashboard-panel">
            <h2>{tr('reportByStudent')}</h2>
            <ul className="reports-students">
              {report.students.filter(entry => entry.lessons > 0).map(entry => (
                <li key={entry.name}>
                  <span className="reports-student-name">{displayName(entry.name)}</span>
                  <span className="reports-student-meta"><bdi>{entry.lessons}</bdi></span>
                  <MoneyText amount={entry.billed} />
                </li>
              ))}
            </ul>
          </section>

          <section className="dashboard-panel">
            <h2>{tr('reportByWeekday')}</h2>
            <ul className="reports-weekdays">
              {report.schedule.byWeekday.map(entry => (
                <li key={entry.weekday}>
                  <span>{tr(WEEKDAY_KEYS[entry.weekday as Weekday])}</span>
                  <span
                    className="reports-bar"
                    style={{ inlineSize: `${barWidth(entry.lessons, report)}%` }}
                    aria-hidden="true"
                  />
                  <bdi>{entry.lessons}</bdi>
                </li>
              ))}
            </ul>
          </section>

          {report.inactiveStudents.length > 0 && (
            <section className="dashboard-panel">
              <h2>{tr('reportInactive')}</h2>
              <p className="dashboard-quiet">{report.inactiveStudents.join(', ')}</p>
            </section>
          )}
        </>
      )}
    </section>
  );
}

/** Relative to the busiest day, so the shape of the week is readable. */
function barWidth(lessons: number, report: MonthlyReportModel): number {
  const busiest = Math.max(...report.schedule.byWeekday.map(entry => entry.lessons), 1);
  return Math.round((lessons / busiest) * 100);
}
